import Personalizer from 'personalizer';
import Conversion from 'utils/Conversion';
import TimeUtil from 'utils/date/TimeUtil';
import NumberParser from 'utils/parsers/NumberParser';

var PaceCalculator = {
    convertPaceToSpeed: function(pace, conversionUnit) {
        if (pace.indexOf(':') === -1) {
            throw 'pace should be in hh:mm:ss format';
        }
        pace = Conversion.convertPaceToSpeed(conversionUnit, TimeUtil.timeToMinutes(pace));
        return pace;
    },

    convertPaceToSpeedPersonalized: function(pace, conversionUnit, activityTypeParent) {
        if (pace.indexOf(':') === -1) {
            throw 'pace should be in hh:mm:ss format';
        }
        return this.personalizeSpeed(this.convertPaceToSpeed(pace, conversionUnit), activityTypeParent);
    },

    convertToSpeed: function(speed, conversionUnit) {
        return Conversion.convertSpeedToMPS(conversionUnit, this.convertToFloat(speed));
    },

    convertSpeedPersonalized: function(speed, conversionUnit, activityTypeParent) {
        return this.personalizeSpeed(this.convertToSpeed(+speed, conversionUnit), activityTypeParent);
    },

    /**
     *
     * http://www.coolrunning.com/engine/4/4_1/96.shtml
     * @param pace
     * @isMetric
     * @param distanceInMeters
     */
    convertPaceToTime: function(pace, isMetric, distanceInMeters) {
        var mps = this.convertPaceToSpeed(pace, isMetric ? 'kilometer' : 'mile'),
            seconds = this.formatDecimal(distanceInMeters / mps, 8),
            time = Personalizer.personalizeDuration(Math.ceil(seconds), false);
        return {
            time,
            seconds
        };
    },
    /**
     *
     * http://www.calculatoredge.com/civil%20engg%20calculator/speed%20Distance%20Time.htm
     * @param speed
     * @param isMetric
     * @param distanceInMeters
     */
    convertSpeedToTime: function(speed, isMetric, distanceInMeters) {
        var seconds = this.convertSpeedToSeconds(speed, isMetric, distanceInMeters),
            time = Personalizer.personalizeDuration(Math.ceil(seconds), false);
        return {
            time,
            seconds
        };
    },

    convertSpeedToSeconds: function(speed, isMetric, distanceInMeters) {
        var mps, seconds;

        speed = this.convertToFloat(speed);

        if (isMetric) {
            mps = speed * Personalizer.ConversionFactors.KphToMps;
        } else {
            mps = speed * Personalizer.ConversionFactors.MphToMps;
        }

        seconds = this.formatDecimal(distanceInMeters / mps, 8);
        return seconds;
    },

    personalizeSpeed: function(value, activityTypeParent) {
        return Personalizer.personalizeSpeed(value, activityTypeParent, undefined);
    },

    /**
     * Because of some visual discrepancies due to rounding. We need to use the personalized values to calculate
     * the time.
     * @param parentActivityType
     * @param distanceMeter
     * @param speedMeterPerSecond
     * @returns the personalized time
     */
    calculateTime: function(parentActivityType, distanceMeter, speedMeterPerSecond) {
        var ismetric = Personalizer.getMeasurementSystem() === Personalizer.MeasurementSystems.METRIC,
            personalizedSpeed = Personalizer.personalizeSpeed(speedMeterPerSecond, parentActivityType, null),
            time = null;

        if (personalizedSpeed === 0) {
            time = Personalizer.personalizeDuration(0);
        } else {
            if (parentActivityType === 'running') {
                time = this.convertPaceToTime(personalizedSpeed, ismetric, distanceMeter);
                time = time.time;
            } else if (parentActivityType === 'cycling' || parentActivityType === 'other') {
                time = this.convertSpeedToTime(personalizedSpeed, ismetric, distanceMeter);
                time = time.time;
            } else if (parentActivityType === 'swimming') {
                time = this.convertSwimPaceToTime(personalizedSpeed, ismetric, distanceMeter);
            }
        }

        return time;
    },

    /**
     * Because of some visual discrepancies due to rounding. We need to use the personalized values to calculate
     * the time.
     * @param parentActivityType
     * @param distanceMeter
     * @param speedMeterPerSecond
     * @param getZeroIfOverHour function will return a number type of 0 for pace if the pace is over 59:59, default to true
     * @returns the personalized time
     */
    calculateTimeWithActual: function(parentActivityType, distanceMeter, speedMeterPerSecond, getZeroIfOverHour) {
        var ismetric = Personalizer.getMeasurementSystem() === Personalizer.MeasurementSystems.METRIC,
            personalizedSpeed = Personalizer.personalizeSpeed(
                speedMeterPerSecond,
                parentActivityType,
                null,
                getZeroIfOverHour
            ),
            time = null;

        if (personalizedSpeed === 0) {
            time = Personalizer.personalizeDuration(0);
        } else {
            if (parentActivityType === 'running' || parentActivityType.indexOf('_running') > -1) {
                time = this.convertPaceToTime(personalizedSpeed, ismetric, distanceMeter);
            } else if (
                parentActivityType === 'cycling' ||
                parentActivityType.indexOf('_cycling') ||
                parentActivityType === 'other'
            ) {
                time = this.convertSpeedToTime(personalizedSpeed, ismetric, distanceMeter);
            } else if (parentActivityType === 'swimming') {
                time = this.convertSwimPaceToTime(personalizedSpeed, ismetric, distanceMeter);
            }
        }

        return time;
    },

    convertSwimPaceToTime: function(pace, isMetric, distanceInMeters) {
        var mps = this.convertPaceToSpeed(pace, isMetric ? 'meter' : 'yard'),
            seconds = this.formatDecimal(distanceInMeters / mps, 8),
            time = Personalizer.personalizeDuration(Math.ceil(seconds), false);
        return time;
    },

    formatDecimal: function(num, numPlaces) {
        var snum = new String(parseFloat(num)),
            i = 0,
            z = 0,
            sout = '',
            ch = '';

        while (i < snum.length && ch !== '.') {
            ch = snum.charAt(i++);
            sout += ch;
        }

        while (i < snum.length && z++ < numPlaces) {
            ch = snum.charAt(i++);
            sout += ch;
        }

        if (snum.indexOf('.') === -1) {
            sout += '.';
        }

        while (z++ < numPlaces) {
            sout += '0';
        }
        return sout;
    },

    convertToFloat: function(num) {
        try {
            num = new NumberParser({
                numberSeparator: Personalizer.getNumberSeparator()
            }).parse('' + num);
        } catch (err) {
            num = 0;
        }
        return num;
    },

    /**
     * Calculates a distance given speed and time.
     * @param {*} speed in meters per second
     * @param {*} time in hh:mm:ss
     * @returns distance in meters
     */
    getDistanceFromSpeedAndTime(speed, time) {
        let seconds = TimeUtil.hoursMinutesAndSecondsToSeconds(time);
        let distance = speed * seconds;
        return distance;
    }
};

export default PaceCalculator;
